import { useEffect, useRef, type PointerEvent as ReactPointerEvent } from "react";

import { pointerMovedBeyondTapSlop, TOUCH_LIST_BLUR_YIELD_MS, touchPickShouldYieldBlur } from "@/lib/touchOpenClose";

type ActiveTouchListPick = {
  pointerId: number;
  startX: number;
  startY: number;
  scrolled: boolean;
  commit: () => void;
};

/**
 * Commit portaled list options on pointer up for touch, while allowing vertical scroll
 * when the finger moves beyond tap slop. Mouse picks commit on pointer down (legacy desktop).
 *
 * Listeners stay up for the life of the control. Closing the list on blur must not drop
 * the in-flight finger, or tablet taps close the menu without selecting a row.
 */
export function useTouchListPickSession(
  active: boolean,
  options?: { onGestureEnd?: () => void },
): {
  onListItemPointerDown: (event: ReactPointerEvent, commit: () => void) => void;
  isPickPending: () => boolean;
  shouldYieldBlurCommit: () => boolean;
} {
  const pickRef = useRef<ActiveTouchListPick | null>(null);
  const yieldBlurUntilRef = useRef(0);
  const onGestureEndRef = useRef(options?.onGestureEnd);
  onGestureEndRef.current = options?.onGestureEnd;

  function armBlurYield(): void {
    yieldBlurUntilRef.current = Date.now() + TOUCH_LIST_BLUR_YIELD_MS;
  }

  useEffect(() => {
    const onPointerMove = (event: PointerEvent): void => {
      const pick = pickRef.current;
      if (pick == null || pick.pointerId !== event.pointerId || pick.scrolled) {
        return;
      }
      if (pointerMovedBeyondTapSlop(pick.startX, pick.startY, event.clientX, event.clientY)) {
        pick.scrolled = true;
      }
    };
    const finishPick = (event: PointerEvent): void => {
      const pick = pickRef.current;
      if (pick == null || pick.pointerId !== event.pointerId) {
        return;
      }
      pickRef.current = null;
      armBlurYield();
      if (!pick.scrolled) {
        pick.commit();
      }
      onGestureEndRef.current?.();
    };
    const onPointerCancel = (event: PointerEvent): void => {
      if (pickRef.current?.pointerId !== event.pointerId) {
        return;
      }
      pickRef.current = null;
      armBlurYield();
      onGestureEndRef.current?.();
    };
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", finishPick);
    window.addEventListener("pointercancel", onPointerCancel);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", finishPick);
      window.removeEventListener("pointercancel", onPointerCancel);
      pickRef.current = null;
    };
  }, []);

  function onListItemPointerDown(event: ReactPointerEvent, commit: () => void): void {
    if (!active || event.button !== 0) {
      return;
    }
    armBlurYield();
    if (event.pointerType === "mouse") {
      event.preventDefault();
      commit();
      return;
    }
    pickRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      scrolled: false,
      commit,
    };
  }

  function isPickPending(): boolean {
    return pickRef.current != null;
  }

  function shouldYieldBlurCommit(): boolean {
    return touchPickShouldYieldBlur({
      pickPending: isPickPending(),
      nowMs: Date.now(),
      yieldUntilMs: yieldBlurUntilRef.current,
    });
  }

  return { onListItemPointerDown, isPickPending, shouldYieldBlurCommit };
}
